import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { Sparkles } from "lucide-react";

const NAVY = "#0F172A";
const ORANGE = "#EE7F35";
const BLUE = "#3F6DB0";
const SITUATION = "Ich stelle im Team-Meeting mein neues Konzept vor. Einer will nur Zahlen sehen, die anderen schweigen.";
const SATZ = "„Bevor ich ins Detail gehe: Hier sind die drei Zahlen, auf die es ankommt.“";
const ICONS = ["katze", "eule", "hund", "biene"];
// Szenen: 0 tippen, 1 analyse, 2 fokustyp, 3 satz, 4 abschluss
const DURATIONS = [4000, 2000, 3000, 4000, 1500];

const useLoop = (running, reduced) => {
  const [phase, setPhase] = useState(reduced ? 3 : 0);
  useEffect(() => {
    if (reduced || !running) return undefined;
    const id = setTimeout(() => setPhase((p) => (p + 1) % DURATIONS.length), DURATIONS[phase]);
    return () => clearTimeout(id);
  }, [phase, running, reduced]);
  return phase;
};

const Typed = ({ active, reduced }) => {
  const [n, setN] = useState(reduced ? SITUATION.length : 0);
  useEffect(() => {
    if (reduced) return undefined;
    if (!active) { setN(SITUATION.length); return undefined; }
    setN(0);
    const id = setInterval(() => setN((x) => (x >= SITUATION.length ? x : x + 2)), 3400 / (SITUATION.length / 2));
    return () => clearInterval(id);
  }, [active, reduced]);
  return <span data-testid="pc-demo-situation">{SITUATION.slice(0, n)}{active && n < SITUATION.length && <span className="animate-pulse">|</span>}</span>;
};

const Analyse = () => {
  const [i, setI] = useState(0);
  useEffect(() => { const id = setInterval(() => setI((x) => (x + 1) % 4), 280); return () => clearInterval(id); }, []);
  return (
    <div className="flex flex-col items-center gap-3 py-4" data-testid="pc-demo-analyse">
      <div className="flex gap-3">
        {ICONS.map((k, idx) => (
          <img key={k} src={`/icons/icon-${k}.png`} alt="" className={`w-10 h-10 transition-opacity duration-200 ${idx === i ? "opacity-100" : "opacity-25"}`} />
        ))}
      </div>
      <p className="text-xs text-slate-500">Wird analysiert …</p>
    </div>
  );
};

const card = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0 }, transition: { duration: 0.45 } };

const Ergebnis = ({ showSatz }) => (
  <div className="space-y-3">
    <motion.div {...card} className="rounded-2xl bg-[#DCE7F7] p-3 flex gap-3 items-center" data-testid="pc-demo-fokustyp">
      <img src="/icons/icon-eule.png" alt="" className="w-11 h-11 shrink-0" />
      <p className="text-xs leading-snug text-slate-700">Dein Gegenüber denkt vermutlich als <b style={{ color: NAVY }}>Eule</b>: Er will den Zusammenhang und die Logik verstehen.</p>
    </motion.div>
    {showSatz && (
      <motion.div {...card} className="rounded-2xl bg-white border-2 p-3" style={{ borderColor: ORANGE }} data-testid="pc-demo-satz">
        <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: ORANGE }}>Dein erster Satz</p>
        <p className="mt-1 text-sm font-semibold leading-snug" style={{ color: NAVY }}>{SATZ}</p>
      </motion.div>
    )}
  </div>
);

export const PocketCoachDemo = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.4 });
  const reduced = !!useReducedMotion();
  const phase = useLoop(inView, reduced);
  return (
    <div ref={ref} className="relative mx-auto w-[270px] rounded-[2.2rem] bg-[#0F172A] p-2.5 shadow-2xl" data-testid="pc-demo" data-phase={phase}>
      <div className="rounded-[1.8rem] bg-[#F8FAFC] h-[440px] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <p className="text-sm font-extrabold" style={{ color: BLUE }}>PocketCoach</p>
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400" data-testid="pc-demo-beispiel">Beispiel</span>
        </div>
        <div className="mx-3 rounded-2xl bg-white border border-slate-200 p-3 text-[13px] leading-snug text-slate-700 min-h-[92px]">
          <Typed active={phase === 0} reduced={reduced} />
        </div>
        <div className="flex-1 px-3 pt-3">
          <AnimatePresence mode="wait">
            {phase === 1 && <motion.div key="a" {...card}><Analyse /></motion.div>}
            {(phase === 2 || phase === 3) && <motion.div key="e" {...card}><Ergebnis showSatz={phase === 3} /></motion.div>}
            {phase === 4 && (
              <motion.div key="s" {...card} className="h-full flex flex-col items-center justify-center text-center gap-2" data-testid="pc-demo-abschluss">
                <Sparkles className="w-6 h-6" style={{ color: ORANGE }} />
                <p className="text-base font-extrabold" style={{ color: NAVY }}>Für jede Situation.<br />Jederzeit.</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
